import {
	ChangeDetectionStrategy,
	Component,
	ElementRef,
	ViewChild,
	computed,
	effect,
	input,
	output,
	signal
} from '@angular/core';
import { ImageGalleryApi } from 'api/image-gallery/image-gallery-api.js';
import { CosmosDb } from 'lib/cosmos/cosmosDb.js';
import { filesHelper } from 'lib/helpers/files-helper.js';
import { stringHelper } from 'lib/helpers/string-helper.class.js';
import { IButton } from 'lib/ui/buttons/index.js';
import { ImageAspectRatio } from 'lib/ui/crop/index.js';
import { DialogService } from 'lib/ui/dialog/index.js';
import { IImage } from 'lib/ui/ui-models.js';
import { Observable, catchError, map } from 'rxjs';
import { CoreAppService } from 'src/app/core/core-app.service.js';
import { FormFieldComponent } from '../form-field.component.js';
import { SelectItemField } from './select-item-field.component.js';

export type FileFormImageGalleryDisplayMode = 'inline' | 'dialog';

export interface IFileFormFieldCropOptions {
	aspectRatio: ImageAspectRatio;
	resizeToWidth?: number;
	resizeToHeight?: number;
}

interface IImageGalleryItem {
	id: string;
	fileReference: CosmosDb.ICosmosFileReference;
	image: IImage;
	tag: 'custom' | 'default';
}

@Component({
	selector: 'ui-file-form-field',
	templateUrl: './file-form-field.component.html',
	styleUrls: ['../form-fields.shared.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class FileFormFieldComponent extends FormFieldComponent<CosmosDb.ICosmosFileReference[]> {
	public readonly validation = input.required<'images' | 'allFiles'>();
	public readonly uploadFunction = input.required<(file: File) => Observable<CosmosDb.ICosmosFileReference[]>>();
	public readonly imageCrop = input<IFileFormFieldCropOptions>();
	public readonly standalone = input<boolean>(false);
	public readonly enableImageGallery = input<boolean>(false);
	public readonly imageGalleryDisplayMode = input<FileFormImageGalleryDisplayMode>('dialog');
	public readonly maxFiles = input<number | undefined>();

	public readonly deleteFile = output<CosmosDb.ICosmosFileReference>();
	public readonly selectFile = output<CosmosDb.ICosmosFileReference>();

	@ViewChild('inputFileElement') inputFileElement?: ElementRef<HTMLInputElement>;

	public readonly isUploading = signal<boolean>(false);
	public readonly file = signal<File | undefined>(undefined);
	public readonly cropFile = signal<boolean>(false);

	public readonly noFileUploadedMessage = computed<string>(() => {
		const maxFiles = this.maxFiles();
		if (this.validation() === 'allFiles') {
			if (maxFiles === 1) {
				return `No file has been uploaded`;
			}
			return `No files have been uploaded`;
		}

		if (maxFiles === 1) {
			return `No image has been uploaded`;
		}
		return `No images have been uploaded`;
	});

	public readonly cannotUploadMoreFilesMessages = computed<string>(() => {
		return '';
	});

	public readonly showNoFileUploadedMessage = computed<boolean>(() => {
		return false;
	});

	public readonly showUploadControl = computed<boolean>(() => {
		const currentValue = this.currentValue();
		const maxFiles = this.maxFiles();

		if (currentValue && maxFiles) {
			if (currentValue.length >= maxFiles) {
				return false;
			}
		}
		return true;
	});

	private readonly imageGalleries = computed<ImageGalleryApi.IImageGalleries | undefined>(() => {
		return this.appDataService.imageGalleries();
	});

	public readonly galleryListItems = computed<SelectItemField<IImageGalleryItem[]>[]>(() => {
		const galleries: SelectItemField[] = [
			{
				title: 'Default image gallery',
				data: this.appGalleryItems() ?? [],
				value: 'default'
			},
			{
				title: 'Previously uploaded',
				data: this.customGalleryItems() ?? [],
				value: 'previously_uploaded'
			}
		];

		return galleries;
	});

	public readonly galleryButtons = computed<IButton[]>(() => {
		const buttons: IButton[] = this.galleryListItems().map((m) => {
			const button: IButton = {
				title: m.title,
				buttonSize: 'default',
				click: () => this.openImageGalleryDialog(m),
				icon: 'gallery'
			};

			return button;
		});

		return buttons;
	});

	private readonly customGalleryItems = computed<IImageGalleryItem[] | undefined>(() => {
		return this.allGalleryItems()?.filter((m) => m.tag === 'custom');
	});

	private readonly appGalleryItems = computed<IImageGalleryItem[] | undefined>(() => {
		return this.allGalleryItems()?.filter((m) => m.tag === 'default');
	});

	private readonly allGalleryItems = computed<IImageGalleryItem[] | undefined>(() => {
		const imageGalleries = this.imageGalleries();

		if (!imageGalleries) {
			return undefined;
		}

		const images: IImageGalleryItem[] = [
			...this.mapToImageGalleryItem(imageGalleries.globalGallery.genericImages, 'default'),
			...this.mapToImageGalleryItem(imageGalleries.globalGallery.dietImages, 'default'),
			...this.mapToImageGalleryItem(imageGalleries.globalGallery.workoutImages, 'default'),
			...this.mapToImageGalleryItem(imageGalleries.userGallery.userUploadedImages, 'custom')
		];

		return images;
	});

	public readonly imagesColumnsPerRow = computed<number>(() => {
		if (this.isMobileView()) {
			return 3;
		}
		if (this.isTabletView()) {
			return 5;
		}
		return 6;
	});

	public readonly activeGalleryValue = signal<string | undefined>(this.galleryListItems()?.[0]?.value);

	public readonly activeGallery = computed<SelectItemField<IImageGalleryItem[]> | undefined>(() => {
		const gallery = this.galleryListItems()?.find((m) => m.value === this.activeGalleryValue());

		if (!gallery) {
			return undefined;
		}

		return gallery;
	});

	constructor(private readonly dialogService: DialogService, private readonly coreAppService: CoreAppService) {
		super();

		effect(() => {
			if (this.enableImageGallery()) {
				super.subscribeToObservable(this.coreAppService.initImageGalleries());
			}
		});
	}

	handleSetActiveGallery(galleryItem: SelectItemField<IImageGalleryItem[]>): void {
		this.activeGalleryValue.set(galleryItem.value);
	}

	handleImageCropped(croppedImage: File): void {
		// upload cropped file
		this.uploadFile(croppedImage);
	}

	onFilechange(event: any): void {
		if (event.target.files) {
			const file = event.target.files[0];

			this.file.set(file);

			if (this.imageCrop()) {
				const canCrop = filesHelper.isImageFile(file.name);
				this.cropFile.set(canCrop);
			}
		}
	}

	openImageGalleryDialog(galleryListItem: SelectItemField<IImageGalleryItem[]>): void {
		const galleryItems = galleryListItem.data;

		if (!galleryItems.length) {
			return;
		}

		this.dialogService.openImagesDialog({
			images: galleryItems.map((m) => m.image),
			title: `Select image`,
			onSelect: (image) => {
				const galleryItem = galleryItems.find(
					(m) => m.image.originalImage.absoluteUrl === image.originalImage.absoluteUrl
				);

				if (galleryItem) {
					this.handleSelectGalleryItem(galleryItem);
				}
			}
		});
	}

	handleSelectGalleryItem(galleryItem: IImageGalleryItem): void {
		super.updateValue([galleryItem.fileReference]);
		this.selectFile.emit(galleryItem.fileReference);
	}

	handleUpload(): void {
		const file = this.file();
		if (!file || !this.uploadFunction) {
			return;
		}

		this.uploadFile(file);
	}

	removeFile(fileReference: CosmosDb.ICosmosFileReference): void {
		this.deleteFile.emit(fileReference);

		const currentValue = this.currentValue?.();
		if (currentValue) {
			const newValue = currentValue.filter((m) => m.absoluteUrl !== fileReference.absoluteUrl);
			super.updateValue(newValue);
		}
	}

	protected getHint(value?: CosmosDb.ICosmosFileReference[]): string | undefined {
		const file = this.file();
		const hints: string[] = [];

		if (!this.showUploadControl()) {
			return undefined;
		}

		if (this.isValueValid(value)) {
			if (this.validation() === 'images') {
				hints.push(`${filesHelper.imageExtensions.join(', ')}`);
			}
			if (this.maxFiles()) {
				hints.push(`Max files: ${this.maxFiles()}`);
			}
		} else {
			if (!file && this.validation() === 'images') {
				/* empty */
			} else if (file && !this.isAllowedExtension(file)) {
				hints.push(`Invalid file extension '${stringHelper.getExtension(file.name)?.toLowerCase()}'`);
			}

			if (this.maxFiles()) {
				hints.push(`You can upload '${this.maxFiles()}' file at maximum`);
			}
		}

		return hints.join('<br>');
	}

	protected isValueValid(value?: CosmosDb.ICosmosFileReference[]): boolean {
		const file = this.file();
		const maxFiles = this.maxFiles();

		if (file) {
			const extensionAllowed = this.isAllowedExtension(file);

			if (!extensionAllowed) {
				return false;
			}
		}

		if (maxFiles && value) {
			if (value.length > maxFiles) {
				return false;
			}
		}

		return true;
	}

	private mapToImageGalleryItem(
		files: CosmosDb.ICosmosFileReference[],
		tag: 'default' | 'custom'
	): IImageGalleryItem[] {
		return files
			.filter((m) => m.image)
			.map((m) => {
				const item: IImageGalleryItem = {
					id: m.absoluteUrl,
					tag: tag,
					fileReference: m,
					image: m.image as IImage
				};

				return item;
			});
	}

	private isAllowedExtension(file: File): boolean {
		if (this.validation() === 'images') {
			const isImage = filesHelper.isImageFile(file.name);

			if (isImage) {
				return true;
			}

			return false;
		}

		return true;
	}

	private uploadFile(file: File): void {
		if (this.isUploading()) {
			// upload already in process
			return;
		}

		this.isUploading.set(true);
		super.subscribeToObservable(
			this.uploadFunction()(file).pipe(
				map((fileReferences) => {
					const newValue: CosmosDb.ICosmosFileReference[] = fileReferences;

					super.updateValue(newValue);

					this.clearFile();
					this.isUploading.set(false);
				}),
				catchError((err) => {
					this.isUploading.set(false);
					throw err;
				})
			)
		);
	}

	private clearFile(): void {
		if (this.inputFileElement?.nativeElement) {
			this.inputFileElement.nativeElement.value = '';
		}

		this.file.set(undefined);
	}
}
